import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { currentProfileAtom } from "../../../features/Club/atoms";
import {
  WhooshMutationError,
  useMutationWithError,
} from "../../../utils/hooks/useMutationWithError";
import {
  AddEventSignUpToWaitlistInput,
  useAddEventSignUpToWaitlistMutation,
  useAddEventSignUpToWaitlistMutation$data,
} from "./__generated__/useAddEventSignUpToWaitlistMutation.graphql";

export const useAddEventSignUpToWaitlist = (
  successCallback?: (signUpId?: string | null) => void
) => {
  const { showToast } = useToast();

  const currentProfile = useAtomValue(currentProfileAtom);
  const memberId = currentProfile?.memberId;

  const [addEventSignUp, isAdding] =
    useMutationWithError<useAddEventSignUpToWaitlistMutation>(
      useAddEventSignUpToWaitlistMutationString
    );

  const onCompleted = useCallback(
    (res: useAddEventSignUpToWaitlistMutation$data, isDraft?: boolean) => {
      if (res.responsePayload?.success) {
        if (!isDraft) {
          showToast({
            description: t`Registration Successfully Created.`,
            semanticStyle: "success",
            toastId: "create-registration-success",
          });
        }
        successCallback?.(res.responsePayload.event?.signUpForClient?.id);
      }
    },
    [showToast, successCallback]
  );

  const onCompletedErrorCustom = useCallback(
    (errors: WhooshMutationError[]) => {
      if (errors.find((e) => e.code === "already_taken")) {
        showToast({
          description: t`One of your group members is already signed up for the event. Please remove them from your group and try again.`,
          toastId: "create-registration-error-already-taken",
          semanticStyle: "error",
        });
      } else {
        showToast({
          description: t`Sorry. An error occurred while creating your registration.`,
          toastId: "create-registration-error",
          semanticStyle: "error",
        });
      }

      return [];
    },
    [showToast]
  );

  const handleAddEventSignUpToWaitlist = useCallback(
    (input: AddEventSignUpToWaitlistInput) => {
      const isDraft = input.eventSignUp?.isDraft === true;

      if (memberId)
        addEventSignUp({
          variables: { input, memberId },
          onCompleted: (response: useAddEventSignUpToWaitlistMutation$data) =>
            onCompleted(response, isDraft),
          onCompletedErrorCustom,
        });
    },
    [addEventSignUp, memberId, onCompleted, onCompletedErrorCustom]
  );

  return [handleAddEventSignUpToWaitlist, isAdding] as const;
};

const useAddEventSignUpToWaitlistMutationString = graphql`
  mutation useAddEventSignUpToWaitlistMutation(
    $input: AddEventSignUpToWaitlistInput!
    $memberId: ID!
  ) {
    responsePayload: addEventSignUpToWaitlist(input: $input) {
      errors {
        field
        code
        message
      }
      success
      event {
        id
        accessStatusForClient(clientId: $memberId)
        signUpStatusForClient(clientId: $memberId)
        eventSeries {
          events {
            edges {
              node {
                accessStatusForClient(clientId: $memberId)
                signUpStatusForClient(clientId: $memberId)
              }
            }
          }
        }
        signUpForClient(clientId: $memberId) {
          id
        }
        ...EventDetailAttendeesList
        ...EventDetailGroupDisplay_event
      }
    }
  }
`;
